<template>
  <card :href="href" @mouseleave="reset">
    <template #header>
      <h2 class="font-medium text-blue dark:text-white">
        Graduating class outcomes
      </h2>
    </template>
    <div class="pt-4 pr-4 pl-1.5 bg-gray-50 dark:bg-gray-800/50 aspect-[4/3] border-b border-gray-300 dark:border-gray-800">
      <div :id="id" class="h-full"/>
    </div>
    <div class="highcharts-description text-sm px-1 py-2">
      <div class="flex justify-between py-1 px-3">
        {{ active.graduation_year }} grads
      </div>
      <div
        class="flex items-center justify-between gap-2 py-1 px-3"
      >
        <div class="flex gap-2 items-center">
          <div class="size-2 rounded-full bg-blue-400"/>
          Long-term full-time bar passage required jobs
        </div>
        <div class="font-semibold text-blue dark:text-white">
          {{ active.bar_passage_required.percent }}%
        </div>
      </div>
      <div
        class="flex items-center justify-between gap-2 py-1 px-3"
      >
        <div class="flex gap-2 items-center">
          <div class="size-2 rounded-full bg-yellow-400"/>
          Long-term full-time JD adv or pro jobs
        </div>
        <div class="font-semibold text-blue dark:text-white">
          {{ active.jd_adv_pro.percent }}%
        </div>
      </div>
      <div
        class="flex items-center justify-between gap-2 py-1 px-3"
      >
        <div class="flex gap-2 items-center">
          <div class="size-2 rounded-full bg-orange-400"/>
          All other grads
        </div>
        <div class="font-semibold text-blue dark:text-white">
          {{ active.other.percent }}%
        </div>
      </div>
    </div>
  </card>
</template>

<script setup>
import Card from "@/components/cards/Card.vue"
import Highcharts from "@/highcharts"
import colors from "@/colors"
import route from "ziggy-js"
import { computed, onMounted, ref } from "vue"

const props = defineProps({
  school: { type: Object, required: true },
})

const id = "job_type_over_time"
const href = route("trends.jobs", ["the-job-search"])

const hover = (event) => {
  active.value = event.point
}

const jobOutcomes = computed(() => props.school.jobs_by_longterm_fulltime_employment)

const active = ref(jobOutcomes.value[jobOutcomes.value.length - 1])

const reset = () => {
  active.value = jobOutcomes.value[jobOutcomes.value.length - 1]
}

onMounted(() => {
  const typeSeries = []

  typeSeries.push(
    {
      type: "column",
      stacking: "normal",
      animation: false,
      data: jobOutcomes.value.map((outcome) => ({
        ...outcome,
        x: outcome.graduation_year,
        y: outcome.other.count,
      })),
      stack: "a",
      color: colors.orange[400],
      name: "All other grads",
    },
    {
      type: "column",
      stacking: "normal",
      animation: false,
      data: jobOutcomes.value.map((outcome) => ({
        ...outcome,
        x: outcome.graduation_year,
        y: outcome.jd_adv_pro.count,
      })),
      stack: "a",
      color: colors.yellow[400],
      name: "Long-term full-time JD adv or pro jobs",
    },
    {
      type: "column",
      stacking: "normal",
      animation: false,
      data: jobOutcomes.value.map((outcome) => ({
        ...outcome,
        x: outcome.graduation_year,
        y: outcome.bar_passage_required.count,
      })),
      stack: "a",
      color: colors.blue[400],
      name: "Long-term full-time bar passage required jobs",
    },
  )

  const options = {
    chart: {
      type: "column",
      zoomType: "xy",
      events: {
        mouseOut() {
        },
      },
    },
    legend: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    title: {
      text: "",
    },
    xAxis: {
      categories: jobOutcomes.value.map((e) => e.graduation_year),
    },
    yAxis: {
      min: 0,
      title: {
        text: "Number of graduates",
      },
    },
    tooltip: {
      formatter() {
        hover(this)

        const { series, y, point, percentage, x } = this

        if (series.index == 2) {
          return `${y} out of ${point.stackTotal} grads (${Math.round(percentage * 10) / 10}%) in ${x} had long-term full-time bar passage required jobs`
        }

        if (series.index == 1) {
          return `${y} out of ${point.stackTotal} grads (${Math.round(percentage * 10) / 10}%) in ${x} had long-term full-time JD adv or pro jobs`
        }

        return `All other grads: ${y} out of ${point.stackTotal} (${Math.round(percentage * 10) / 10}%)`
      },
    },
    plotOptions: {
      column: {
        pointPadding: 0.2,
        borderWidth: 0,
        accessibility: {
          point: {
            descriptionFormatter(point) {
              return `${point.series.name}: ${point.y} out of ${point.stackTotal} grads in ${point.x}`
            },
          },
          enabled: true,
        },
      },
      stacking: "normal",

    },
    series: typeSeries,
  }

  new Highcharts.Chart(id, options)
})
</script>
